import {
  type DynamicClient,
  assertWalletAccountSigningAvailability,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  createWalletProviderEventEmitter,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import type { StarknetWindowObject } from '@starknet-io/get-starknet-core';
import starknetCore from '@starknet-io/get-starknet-core';
import { RpcProvider, WalletAccount, type constants } from 'starknet';

import type { StarknetWalletProvider } from '../../../StarknetWalletProvider.types';
import { formatStarknetTypedDataMessage } from '../../../utils/formatStarknetTypedDataMessage';
import { starknetTransferAmount } from '../../../utils/starknetTransferAmount';

type CreateWalletProviderFromStarknetWalletParams = {
  dynamicClient: DynamicClient;
  starknetWindowProvider: StarknetWindowObject;
};

export const createWalletProviderFromStarknetWallet = ({
  dynamicClient,
  starknetWindowProvider,
}: CreateWalletProviderFromStarknetWalletParams): StarknetWalletProvider => {
  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain: 'STARK',
    displayName: starknetWindowProvider.name,
    walletProviderType,
  });

  const connect: StarknetWalletProvider['connect'] = async () => {
    const accounts = await starknetWindowProvider.request({
      type: 'wallet_requestAccounts',
    });

    return {
      addresses: accounts.map((address) => ({
        address,
      })),
    };
  };

  const disconnect: StarknetWalletProvider['disconnect'] = async () => {
    try {
      await starknetWindowProvider.request({
        type: 'wallet_disconnect',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    } catch {
      // Do nothing, wallet is missing `wallet_disconnect` method
    }
    await starknetCore.disconnect();
  };

  const getActiveNetworkId: StarknetWalletProvider['getActiveNetworkId'] =
    async () => {
      const chainId = await starknetWindowProvider.request({
        type: 'wallet_requestChainId',
      });

      return {
        networkId: chainId,
      };
    };

  const getConnectedAddresses: StarknetWalletProvider['getConnectedAddresses'] =
    async () => {
      const accounts = await starknetWindowProvider.request({
        params: {
          silent_mode: true,
        },
        type: 'wallet_requestAccounts',
      });

      return {
        addresses: accounts,
      };
    };

  const getStarknetAccount: StarknetWalletProvider['getStarknetAccount'] =
    async ({ walletAccount }) => {
      await assertWalletAccountSigningAvailability(
        { walletAccount },
        dynamicClient
      );

      const { networkData } = await getActiveNetworkData(
        { walletAccount },
        dynamicClient
      );

      assertDefined(networkData, 'No active network data found');

      const rpcProvider = new RpcProvider({
        nodeUrl: networkData.rpcUrls.http[0],
      });

      const account = await WalletAccount.connectSilent(
        rpcProvider,
        starknetWindowProvider
      );

      return { account };
    };

  const signMessage: StarknetWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      {
        walletAccount,
      },
      dynamicClient
    );

    const { networkId } = await getActiveNetworkId();

    const encodedMessage = formatStarknetTypedDataMessage({
      chainId: networkId as constants.StarknetChainId,
      inputMessage: message,
    });

    const signature = await starknetWindowProvider.request({
      params: encodedMessage,
      type: 'wallet_signTypedData',
    });

    return { signature: signature.join(',') };
  };

  const switchActiveNetwork: StarknetWalletProvider['switchActiveNetwork'] =
    async ({ networkId }) => {
      await starknetWindowProvider.request({
        params: {
          chainId: networkId,
        },
        type: 'wallet_switchStarknetChain',
      });
    };

  const { getEventEmitter, cleanupEventEmitter } =
    createWalletProviderEventEmitter<{
      removeAccountChangeListener: VoidFunction;
      removeNetworkChangeListener: VoidFunction;
    }>({
      removeEventListeners: ({ setupReturnValue }) => {
        assertDefined(setupReturnValue, 'Setup return value not defined');

        setupReturnValue.removeNetworkChangeListener();
        setupReturnValue.removeAccountChangeListener();
      },

      setupEventListeners: ({
        handleAccountsChanged,
        handleNetworkChanged,
      }) => {
        const onAccountsChanged = (accounts: string[] | undefined) => {
          handleAccountsChanged({
            addresses: accounts ?? [],
          });
        };
        starknetWindowProvider.on('accountsChanged', onAccountsChanged);
        const removeAccountChangeListener = () => {
          starknetWindowProvider.off('accountsChanged', onAccountsChanged);
        };

        const onNetworkChanged = (network: string | undefined) => {
          if (!network) {
            return;
          }

          handleNetworkChanged({
            networkId: network.toString(),
          });
        };
        starknetWindowProvider.on('networkChanged', onNetworkChanged);
        const removeNetworkChangeListener = () => {
          starknetWindowProvider.off('networkChanged', onNetworkChanged);
        };

        return {
          removeAccountChangeListener,
          removeNetworkChangeListener,
        };
      },

      supportedEvents: ['accountsChanged', 'networkChanged'],
    });

  const terminate: StarknetWalletProvider['terminate'] = async () => {
    cleanupEventEmitter();
  };

  const walletProvider: StarknetWalletProvider = {
    chain: 'STARK',
    connect,
    disconnect,
    get events() {
      return getEventEmitter();
    },
    getActiveNetworkId,
    getConnectedAddresses,
    getStarknetAccount,
    groupKey: formatWalletProviderGroupKey(starknetWindowProvider.name),
    key,
    metadata: {
      displayName: starknetWindowProvider.name,
      icon:
        typeof starknetWindowProvider.icon === 'string'
          ? starknetWindowProvider.icon
          : starknetWindowProvider.icon.light,
    },
    signMessage,
    switchActiveNetwork,
    terminate,
    transferAmount: (args) => starknetTransferAmount(args, dynamicClient),
    walletProviderType,
  };

  return walletProvider;
};
